export type BallotRecordStatus = 'draft' | 'saved';

export interface BallotRecordElectionSummary {
  election_date: string | null;
  election_name: string | null;
  election_type: string | null;
  is_past: boolean;
}

/** Summary row returned by GET /user/ballots. */
export interface BallotRecordSummary {
  id: string;
  status: BallotRecordStatus;
  location_label: string;
  /** True when every race with candidates has a selection (drives the green
   * "complete" status dot). */
  is_complete?: boolean;
  /** True when the user forced completion via the "Complete this ballot"
   * toggle, independent of picks. */
  manually_complete?: boolean;
  is_current_address: boolean;
  /** Full postal address for this ballot's address era (from PII by fingerprint).
   * Absent on older records captured before per-ballot addresses existed. */
  address_full?: string | null;
  /** True for a labeled menu row: past elections plus the address's soonest-
   * upcoming election. Later elections at that address stay tab-only (visible
   * via the ballot view's tabs) until they become the soonest. */
  is_visible_entry?: boolean;
  /** True for the ONE row the selector should highlight for the active selection.
   * When the active ballot is a tab-only future election, this is its address's
   * soonest-upcoming row — so viewing any of an address's tabs keeps it selected. */
  is_active_entry?: boolean;
  elections: BallotRecordElectionSummary[];
  next_election_date?: string | null;
  heart_count: number;
  saved_at?: string | null;
  auto_saved?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface BallotRecordSelections {
  candidates: string[];
  measures: Record<string, string>;
}

/** One election at a record's address, as a tab in the ballot view. Returned in
 * ``BallotRecord.sibling_ballots`` (all elections at that address, oldest first). */
export interface BallotSiblingTab {
  id: string;
  election_date: string | null;
  election_name: string | null;
  election_type: string | null;
  guidance_phase?: string | null;
  is_past: boolean;
  is_visible_entry: boolean;
}

/**
 * Full record returned by GET /user/ballots/{id}. Carries the same election
 * payload shape as the legacy /user/ballot response plus record identity
 * fields, so it can be rendered by SampleBallotView directly.
 */
export interface BallotRecord {
  id: string;
  status: BallotRecordStatus;
  selections?: BallotRecordSelections;
  location_label?: string;
  /** Full postal address for this ballot's address era, recovered from PII.
   * Absent on older records captured before per-ballot addresses existed —
   * callers fall back to ``location_label``. */
  address_full?: string | null;
  /** True when every race with candidates has a selection (green dot). */
  is_complete?: boolean;
  /** True when the user forced completion via the "Complete this ballot"
   * toggle, independent of picks. */
  manually_complete?: boolean;
  is_current_address?: boolean;
  saved_at?: string | null;
  elections?: Array<{ is_past?: boolean; [key: string]: unknown }>;
  next_election_date?: string | null;
  /** Every election at this record's address, as tabs (oldest first). */
  sibling_ballots?: BallotSiblingTab[];
  [key: string]: unknown;
}

/** Visual state for the status dot.
 * ``complete`` = saved with a pick in every race (green), ``savedUpcoming`` =
 * saved but not complete (blue), ``draftUpcoming`` = draft (light orange). */
export type BallotDotState =
  'complete' | 'completePast' | 'savedUpcoming' | 'draftUpcoming' | 'savedPast' | 'draftPast';

export const hasUpcomingElection = (
  elections: Array<{ is_past?: boolean }> | undefined | null
): boolean => (elections ?? []).some((e) => !e.is_past);

export const getBallotDotState = (
  status: BallotRecordStatus,
  upcoming: boolean,
  complete = false
): BallotDotState => {
  // Saved + every race picked wins the green "complete" state.
  if (status === 'saved' && complete) return upcoming ? 'complete' : 'completePast';
  if (upcoming) return status === 'saved' ? 'savedUpcoming' : 'draftUpcoming';
  return status === 'saved' ? 'savedPast' : 'draftPast';
};
